import { FunctionComponent, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Tag from '../../../components/Tag'
import Text from '../../../components/Text'
import { IStripePaymentMethod } from '../../../interfaces/models/user'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import { styles } from "../../../styles/themes/style";
import sepaImage from '../../../assets/img/billing/sepa.png'
import cardImage from '../../../assets/img/billing/card.png'
import Table from '../../../components/Table'
import Checkbox from '../../../components/Checkbox'
import Row from '../../../components/Row'
import { calculateColumnWidth } from '../../../utils'
import { isPaymentMethodExpired } from '../../../utils/cardExpiry'
import useMediaQuery from '../../../components/useMediaQuery'
import SvgFragment from '../../../fragments/SvgFragment'
import TooltipWrapper from '../../../components/TooltipWrapper'

interface IPaymentMethodsProps { 
    onChangeHandler?: (e: string) => void
    $height?: string
    disabled?: boolean
    showCheckbox?: boolean
    notifyOnInit?: boolean
}

const PaymentMethods: FunctionComponent<IPaymentMethodsProps> = ({ onChangeHandler, $height, disabled, showCheckbox=true, notifyOnInit=true }) => {
    const tableRef = useRef<HTMLDivElement>(null)
    const initializedRef = useRef(false)

    const isSmall = useMediaQuery("(max-width: 768px)");

    const { store: { subscription, user, workspace } } = useAuthStore()//todo check sepa representation

    const [ paymentMethods, setPaymentMethods ] = useState<IStripePaymentMethod[]>([])
    const [ selectedMethod, setSelectedMethod ] = useState<IStripePaymentMethod | undefined>(undefined)
    const [ isDisabled, setIsDisabled ] = useState<boolean>(disabled ?? false);

    useEffect(() => {
        setIsDisabled(disabled ?? false)
    }, [disabled])

    useEffect(() => {
        let paymentMethods = []
        if(subscription?.paymentMethods?.length > 0) paymentMethods = subscription.paymentMethods
        //if(user?.stripePaymentMethods?.length > 0) paymentMethods = user.stripePaymentMethods //this should only be read from the  subscription

        // if new card is added durign enrichment, we need to update the payment methods
        if(workspace?.stripePaymentMethods?.length > subscription?.paymentMethods?.length) {
            paymentMethods = workspace.stripePaymentMethods;
        }

        setPaymentMethods(paymentMethods)
        const defaultMethod = paymentMethods.find((paymentMethod: IStripePaymentMethod) => paymentMethod.isDefault)
        if (defaultMethod) {
            setSelectedMethod(defaultMethod)
            if (notifyOnInit && !initializedRef.current) {
                initializedRef.current = true
                onChangeHandler?.(defaultMethod.id)
            }
        }
    }, [subscription, user, workspace])

    //constants
    const cardImg = <img
        src={cardImage}
        alt=''
    />
    const sepaImg = <img
        src={sepaImage}
        alt=''
    />

    const [columns, setColumns] = useState([
        {
            header: "",
            key: ['checkbox'],
            width: '36px',
            show: showCheckbox,
            showSmall: true,
        },
        { header: 'Type', key: ['image'], show: true, showSmall: true },
        { header: 'Brand', key: ['branding'], width: '100%', show: true, showSmall: true },
        { header: 'Default', key: ['default'],  width: '100px', show: true, showSmall: true },
    ])

    useEffect(() => {
        const offsetWidth = tableRef.current?.offsetWidth

        const adjustedColumns = calculateColumnWidth(offsetWidth, columns, isSmall) 
        setColumns(adjustedColumns)

    }, [paymentMethods, isSmall])

    const onSelectHandler = (paymentMethod: IStripePaymentMethod) => {
        setSelectedMethod(paymentMethod)
        onChangeHandler?.(paymentMethod.id)
    }

    return (
        <Container $disabled={isDisabled}>
                <Table
                ref={tableRef}
                $height={$height}
                columns={columns}
                noDataPage={<Text $bold $heading5 $lighter>You don`t have any payment methods.</Text>}
                data={paymentMethods?.sort((a: any, b: any) => {
                    if(a.isDefault) return -1
                    if(b.isDefault) return 1
                    return 0
                }).map((paymentMethod: IStripePaymentMethod) => {
                    const expired = isPaymentMethodExpired(paymentMethod)
                    return (
                        {
                            object: {
                                ...paymentMethod,
                                ...(expired
                                    ? {
                                          backgroundColor: styles.colors.red200,
                                          preserveRowBackgroundOnHover: true,
                                      }
                                    : {}),
                            },
                            checkbox: (
                                <StyledCheckbox
                                    checked={!isDisabled && (selectedMethod ? selectedMethod.id === paymentMethod.id : paymentMethod.isDefault)}
                                    checkboxId={paymentMethod.id}
                                    onClickHandler={(e: any) => { 
                                        e.stopPropagation(); 
                                        onSelectHandler(paymentMethod);
                                    }}
                                />
                            ), 
                            onRowClick: () => onSelectHandler(paymentMethod),
                            image: (
                                <Row alignItems><StyledImage>{paymentMethod.type === 'sepa_debit' ? sepaImg : cardImg}</StyledImage> <Text $capitalize $bold $black>{paymentMethod.type}</Text></Row>
                            ),
                            branding: (
                                <Row alignItems gap="8px" flexWrap={true}>
                                    <Text $black>
                                        <Text $black $capitalize>
                                            {paymentMethod.brand === 'sepa_debit' ? 'sepa' : paymentMethod.brand}
                                        </Text>{' '}
                                        ending with {paymentMethod.last4}
                                    </Text>
                                    {expired && (
                                        <TooltipWrapper tooltipText="Expired" placement="top">
                                            <ExpiredIconWrap>
                                                <SvgFragment type="warning" />
                                            </ExpiredIconWrap>
                                        </TooltipWrapper>
                                    )}
                                </Row>
                            ),
                            default: (
                                <div style={{display: 'flex', justifyContent: 'flex-end'}}>{paymentMethod.isDefault && <StyledTag><Tag title={'Default'} $borderColor={styles.colors.green600} $color={styles.colors.green200} SVGtype={'checkmark'} /></StyledTag>}</div>
                            )
                        }
                    )
                })}
            />
        </Container>
    )
}
const Container = styled.div<{ $disabled?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    ${({ $disabled }) =>
        $disabled &&
        `
          pointer-events: none;
          opacity: 0.6;
        `}
`
const StyledCheckbox = styled(Checkbox)`
    height: fit-content;
`
const StyledTag = styled.div`
    background-color: ${styles.colors.green200};

    & svg {
        width: 20px;
        height: 16px
    }
`
const ExpiredIconWrap = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 0;
    cursor: pointer;

    .svg {
        display: block;
    }
`
const StyledImage = styled.div`
    width: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 6px;
`


export default PaymentMethods